/**
 * WordPress dependencies
 */
import { Fragment } from '@wordpress/element';
import { createHigherOrderComponent } from '@wordpress/compose';
import { addFilter } from '@wordpress/hooks';

const { getBlockType } = wp.blocks;

const { useRef, useEffect } = wp.element;

const { useSelect } = wp.data;

/**
 * External dependencies
 */
import deepEqual from 'deep-equal';
import { throttle } from 'throttle-debounce';
import { has, assign } from 'lodash';

import EditorStyles from '../../components/editor-styles';

const includedBlocks = [
	'gutenify/button',
	'gutenify/container',
	'gutenify/content-toggle-item',
	'gutenify/faqs',
	'gutenify/gallery-carousel',
	'gutenify/icon',
	'gutenify/info-box',
	'gutenify/map',
	'gutenify/post-carousel',
	'gutenify/post-list',
	'gutenify/section-title',
	'gutenify/service',
	'gutenify/slide-item',
	'gutenify/slider',
	'gutenify/star-rating',
	'gutenify/team-member',
	'gutenify/team',
	'gutenify/testimonial',
	'gutenify/testimonials',
	'gutenify/wc-product-list',
	'gutenify/wc-product-carousel',
];

/**
 * Custom Styles Component.
 *
 * @param props
 */
function CustomStylesComponent( props ) {
	const { setAttributes, attributes, clientId, name } = props;

	const { blockSettings } = useSelect( () => ( {
		blockSettings: getBlockType( name ),
	} ), [ name ] );

	const onUpdate = ( checkDuplicates ) => {
		if ( ! has( attributes, `blockClientId` ) || '' === attributes.blockClientId ) {
			setAttributes( {
				blockClientId: clientId,
			} );
		}
		if ( has( blockSettings, `gutenify.customStylesCallback` ) ) {
			const gutenifyStyles = blockSettings.gutenify.customStylesCallback( props );
			const updateAttrs = ! deepEqual( attributes.gutenifyStyles, gutenifyStyles );
			if ( updateAttrs ) {
				setAttributes( {
					blockClientId: clientId,
				} );
				setAttributes( {
					gutenifyStyles,
				} );
			}
		}
	};

	const onUpdateThrottle = throttle( 60, onUpdate );

	const didMountRef = useRef( false );

	useEffect( () => {
		// console.log('test');
		// Did update.
		if ( didMountRef.current ) {
			onUpdateThrottle();

			// Did mount.
		} else {
			didMountRef.current = true;

			onUpdate( true );
		}
	}, [ attributes ] );

	let styles = '';

	// generate custom styles.
	if ( attributes.gutenifyStyles ) {
		styles = attributes.gutenifyStyles;
	}
	if ( ! styles ) {
		return null;
	}
	return <EditorStyles styles={ styles } />;
}

/**
 * Extend block attributes with styles.
 *
 * @param {Object} blockSettings Original block settings.
 * @param {string} name Original block name.
 *
 * @return {Object} Filtered block settings.
 */
function addAttribute( blockSettings, name ) {
	if ( includedBlocks.includes( name ) && ! has( blockSettings, 'attributes.gutenifyStyles' ) ) {
		// Use Lodash's assign to gracefully handle if attributes are undefined
		blockSettings.attributes = assign( blockSettings.attributes, {
			gutenifyStyles: {
				type: 'string',
				default: '',
			},
		} );
	}

	return blockSettings;
}

/**
 * Extend block attributes with styles after block transformation
 *
 * @param {Object} transformedBlock Original transformed block.
 * @param {Object} blocks           Blocks on which transform was applied.
 *
 * @return {Object} Modified transformed block, with layout preserved.
 */
function addAttributeTransform( transformedBlock, blocks ) {
	if ( blocks &&
blocks[ 0 ] &&
blocks[ 0 ].clientId === transformedBlock.clientId &&
blocks[ 0 ].attributes &&
blocks[ 0 ].attributes.gutenifyStyles &&
Object.keys( blocks[ 0 ].attributes.gutenifyStyles ).length
	) {
		Object.keys( blocks[ 0 ].attributes ).forEach( ( attrName ) => {
			if ( /^gutenify/.test( attrName ) ) {
				transformedBlock.attributes[ attrName ] = blocks[ 0 ].attributes[ attrName ];
			}
		} );
	}

	return transformedBlock;
}

/**
 * Override the default edit UI to include a new block inspector control for
 * assigning the custom styles if needed.
 *
 * @param {Function|Component} BlockEdit Original component.
 *
 * @return {string} Wrapped component.
 */
const withNewAttrs = createHigherOrderComponent(
	( BlockEdit ) =>
		function( props ) {
			if ( ! includedBlocks.includes( props.name ) ) {
				return <BlockEdit { ...props } />;
			}
			return (
				<Fragment>
					<BlockEdit { ...props } />
					<CustomStylesComponent { ...props } />
				</Fragment>
			);
		},
	'withNewAttrs'
);

// Init filters.
addFilter( 'blocks.registerBlockType', 'gutenify/styles/additional-attributes', addAttribute );
addFilter( 'blocks.switchToBlockType.transformedBlock', 'gutenify/styles/additional-attributes', addAttributeTransform );
addFilter( 'editor.BlockEdit', 'gutenify/styles/additional-attributes', withNewAttrs );
